// Registration Periods Manager — first entry in the future
// Administration → Configuration area. Admins (and MAVIS via
// propose_registration_period_change) manage the tabs that appear across
// Paid Registrations, Interest Forms, and Executive Enrollment.
import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Badge } from "@/components/ui/badge";
import {
  Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle, DialogTrigger,
} from "@/components/ui/dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import { toast } from "@/hooks/use-toast";
import {
  ArrowDown, ArrowUp, CalendarClock, Loader2, Plus, Star, Trash2,
} from "lucide-react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { formatPeriodLabel, type PeriodType } from "@/lib/registrationPeriodLabel";

interface Period {
  id: string;
  name: string;
  display_label: string | null;
  sort_order: number;
  start_date: string | null;
  end_date: string | null;
  is_default: boolean;
  is_visible: boolean;
  is_active: boolean;
  semester: "summer" | "fall" | "spring" | null;
  notes: string | null;
  period_type: PeriodType | null;
  month_number: number | null;
  year: number | null;
  academic_year: string | null;
}

const emptyDraft: Omit<Period, "id"> = {
  name: "",
  display_label: "",
  sort_order: 100,
  start_date: null,
  end_date: null,
  is_default: false,
  is_visible: true,
  is_active: true,
  semester: null,
  notes: null,
  period_type: "custom",
  month_number: null,
  year: new Date().getFullYear(),
  academic_year: null,
};

export function RegistrationPeriodsManager() {
  const [rows, setRows] = useState<Period[] | null>(null);
  const [counts, setCounts] = useState<Record<string, number>>({});
  const [addOpen, setAddOpen] = useState(false);
  const [draft, setDraft] = useState<Omit<Period, "id">>(emptyDraft);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    const { data } = await (supabase as any)
      .from("registration_periods")
      .select("*")
      .order("sort_order");
    setRows((data as Period[]) ?? []);
  };

  const loadCounts = async () => {
    // Simple per-period counts derived from date-in-range of class_payments
    const { data } = await supabase
      .from("class_payments")
      .select("id, created_at")
      .eq("payment_status", "paid");
    const paid = data ?? [];
    if (!rows) return;
    const c: Record<string, number> = {};
    for (const p of rows) {
      if (!p.start_date || !p.end_date) continue;
      const s = new Date(p.start_date).getTime();
      const e = new Date(p.end_date).getTime() + 86_400_000;
      c[p.id] = paid.filter((r: any) => {
        const t = new Date(r.created_at).getTime();
        return t >= s && t <= e;
      }).length;
    }
    setCounts(c);
  };

  useEffect(() => { load(); }, []);
  useEffect(() => { if (rows) loadCounts(); }, [rows]);

  useEffect(() => {
    const ch = supabase
      .channel("registration-periods-mgr")
      .on("postgres_changes", { event: "*", schema: "public", table: "registration_periods" }, () => load())
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const update = async (id: string, patch: Partial<Period>) => {
    const { error } = await (supabase as any)
      .from("registration_periods")
      .update(patch)
      .eq("id", id);
    if (error) {
      toast({ title: "Update failed", description: error.message, variant: "destructive" });
      return;
    }
  };

  const setDefault = async (id: string) => {
    // Enforce single default: clear existing, then set.
    await (supabase as any).from("registration_periods").update({ is_default: false }).eq("is_default", true);
    await update(id, { is_default: true });
    toast({ title: "Default updated" });
  };

  const move = async (id: string, dir: -1 | 1) => {
    if (!rows) return;
    const idx = rows.findIndex((r) => r.id === id);
    const swap = rows[idx + dir];
    if (!swap) return;
    const a = rows[idx];
    await update(a.id, { sort_order: swap.sort_order });
    await update(swap.id, { sort_order: a.sort_order });
    load();
  };

  const remove = async (id: string) => {
    if (!confirm("Delete this registration period? This cannot be undone.")) return;
    const { error } = await (supabase as any).from("registration_periods").delete().eq("id", id);
    if (error) {
      toast({ title: "Delete failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Period deleted" });
  };

  const create = async () => {
    if (!draft.name.trim()) {
      toast({ title: "Name is required", variant: "destructive" });
      return;
    }
    setSaving(true);
    const nextSort = (rows?.at(-1)?.sort_order ?? 0) + 10;
    const payload = { ...draft, sort_order: nextSort };
    if (payload.is_default) {
      await (supabase as any).from("registration_periods").update({ is_default: false }).eq("is_default", true);
    }
    const { error } = await (supabase as any).from("registration_periods").insert(payload);
    setSaving(false);
    if (error) {
      toast({ title: "Create failed", description: error.message, variant: "destructive" });
      return;
    }
    toast({ title: "Period created" });
    setDraft(emptyDraft);
    setAddOpen(false);
  };

  const hasRows = rows && rows.length > 0;

  return (
    <Card>
      <CardHeader className="flex flex-row items-center gap-2">
        <CalendarClock className="h-5 w-5 text-primary" />
        <div className="flex-1">
          <CardTitle className="text-base">Registration Periods</CardTitle>
          <p className="text-xs text-muted-foreground mt-0.5">
            Controls the month/semester tabs across Paid Registrations, Interest Forms, and Executive Enrollment.
          </p>
        </div>
        <Dialog open={addOpen} onOpenChange={setAddOpen}>
          <DialogTrigger asChild>
            <Button size="sm"><Plus className="h-4 w-4 mr-1" /> Add Period</Button>
          </DialogTrigger>
          <DialogContent>
            <DialogHeader><DialogTitle>New Registration Period</DialogTitle></DialogHeader>
            <div className="space-y-3">
              <div>
                <Label>Name</Label>
                <Input value={draft.name} onChange={(e) => setDraft({ ...draft, name: e.target.value })} placeholder="e.g. Fall Semester" />
              </div>
              <div>
                <Label>Display Label</Label>
                <Input value={draft.display_label ?? ""} onChange={(e) => setDraft({ ...draft, display_label: e.target.value })} placeholder="Optional — shown in tabs" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Type</Label>
                  <Select
                    value={draft.period_type ?? "custom"}
                    onValueChange={(v) => setDraft({ ...draft, period_type: v as PeriodType })}
                  >
                    <SelectTrigger><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="monthly">Monthly</SelectItem>
                      <SelectItem value="fall_semester">Fall Semester</SelectItem>
                      <SelectItem value="spring_semester">Spring Semester</SelectItem>
                      <SelectItem value="custom">Custom</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div>
                  <Label>Preview label</Label>
                  <div className="h-9 flex items-center px-3 rounded-md border bg-muted text-sm text-muted-foreground">
                    {formatPeriodLabel(draft)}
                  </div>
                </div>
              </div>
              {draft.period_type === "monthly" && (
                <div className="grid grid-cols-2 gap-3">
                  <div>
                    <Label>Month (1–12)</Label>
                    <Input type="number" min={1} max={12} value={draft.month_number ?? ""} onChange={(e) => setDraft({ ...draft, month_number: e.target.value ? Number(e.target.value) : null })} />
                  </div>
                  <div>
                    <Label>Year</Label>
                    <Input type="number" value={draft.year ?? ""} onChange={(e) => setDraft({ ...draft, year: e.target.value ? Number(e.target.value) : null })} />
                  </div>
                </div>
              )}
              {(draft.period_type === "fall_semester" || draft.period_type === "spring_semester") && (
                <div>
                  <Label>Academic Year (e.g. 2026-2027)</Label>
                  <Input value={draft.academic_year ?? ""} onChange={(e) => setDraft({ ...draft, academic_year: e.target.value || null })} placeholder="2026-2027" />
                </div>
              )}
              <div>
                <Label>Display Label (optional override)</Label>
                <Input value={draft.display_label ?? ""} onChange={(e) => setDraft({ ...draft, display_label: e.target.value })} placeholder="Only used for Custom periods" />
              </div>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Label>Start date</Label>
                  <Input type="date" value={draft.start_date ?? ""} onChange={(e) => setDraft({ ...draft, start_date: e.target.value || null })} />
                </div>
                <div>
                  <Label>End date</Label>
                  <Input type="date" value={draft.end_date ?? ""} onChange={(e) => setDraft({ ...draft, end_date: e.target.value || null })} />
                </div>
              </div>
              <div className="flex items-center gap-4 flex-wrap">
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={draft.is_visible} onCheckedChange={(v) => setDraft({ ...draft, is_visible: v })} /> Visible
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={draft.is_active} onCheckedChange={(v) => setDraft({ ...draft, is_active: v })} /> Active
                </label>
                <label className="flex items-center gap-2 text-sm">
                  <Switch checked={draft.is_default} onCheckedChange={(v) => setDraft({ ...draft, is_default: v })} /> Default
                </label>
              </div>
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setAddOpen(false)}>Cancel</Button>
              <Button onClick={create} disabled={saving}>
                {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : "Create Period"}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </CardHeader>
      <CardContent>
        {rows === null ? (
          <div className="flex items-center gap-2 text-sm text-muted-foreground">
            <Loader2 className="h-4 w-4 animate-spin" /> Loading…
          </div>
        ) : !hasRows ? (
          <p className="text-sm text-muted-foreground">No registration periods yet. Click "Add Period" to create one.</p>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Name</TableHead>
                <TableHead>Dates</TableHead>
                <TableHead>Sort</TableHead>
                <TableHead>Default</TableHead>
                <TableHead>Visible</TableHead>
                <TableHead>Active</TableHead>
                <TableHead className="text-right">Paid regs</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {rows.map((p, i) => (
                <TableRow key={p.id}>
                  <TableCell>
                    <div className="font-medium">{formatPeriodLabel(p)}</div>
                    {p.name !== formatPeriodLabel(p) && (
                      <div className="text-xs text-muted-foreground">Internal: {p.name}</div>
                    )}
                  </TableCell>
                  <TableCell className="text-xs text-muted-foreground">
                    {p.start_date ?? "—"} → {p.end_date ?? "—"}
                  </TableCell>
                  <TableCell>
                    <div className="flex items-center gap-1">
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === 0} onClick={() => move(p.id, -1)}>
                        <ArrowUp className="h-3 w-3" />
                      </Button>
                      <span className="text-xs tabular-nums w-6 text-center">{p.sort_order}</span>
                      <Button size="icon" variant="ghost" className="h-6 w-6" disabled={i === rows.length - 1} onClick={() => move(p.id, 1)}>
                        <ArrowDown className="h-3 w-3" />
                      </Button>
                    </div>
                  </TableCell>
                  <TableCell>
                    <Button
                      size="sm" variant={p.is_default ? "default" : "ghost"}
                      className="h-7"
                      onClick={() => setDefault(p.id)}
                      aria-label={p.is_default ? "Current default" : "Set as default"}
                    >
                      <Star className={`h-3.5 w-3.5 ${p.is_default ? "fill-current" : ""}`} />
                    </Button>
                  </TableCell>
                  <TableCell>
                    <Switch checked={p.is_visible} onCheckedChange={(v) => update(p.id, { is_visible: v })} />
                  </TableCell>
                  <TableCell>
                    <Switch checked={p.is_active} onCheckedChange={(v) => update(p.id, { is_active: v })} />
                    {!p.is_active && <Badge variant="secondary" className="ml-2 text-[10px]">Past</Badge>}
                  </TableCell>
                  <TableCell className="text-right tabular-nums text-sm">
                    {counts[p.id] ?? 0}
                  </TableCell>
                  <TableCell className="text-right">
                    <Button size="icon" variant="ghost" className="h-7 w-7 text-destructive" onClick={() => remove(p.id)}>
                      <Trash2 className="h-3.5 w-3.5" />
                    </Button>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        <p className="text-xs text-muted-foreground mt-3">
          Tip: Ask the Silver Sage Assistant "add a Fall Semester tab" or "make August the default" and it will open a proposal here.
        </p>
      </CardContent>
    </Card>
  );
}

export default RegistrationPeriodsManager;
